import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { getItemFromStorage } from "../../../helper/storage";
import { getAccesses } from '../../../redux/actions/accesses';
import { isUserAuthenticated } from "../../../redux/actions/authentication";
import { getDemo } from "../../../redux/actions/demo";
import { addInvestors, getEmployees } from "../../../redux/actions/employees";
import { getManagers } from "../../../redux/actions/managers";
import PopUpSuccess from "../../global/PopUpSuccess/PopUpSuccess";
import InvitationByCsv from "../../invitation/InvitationByCsv/InvitationByCsv";
import AccessHeader from "../AccessHeader/AccessHeader";
import EmployeesFilters from "../EmployeesFilters/EmployeesFilters";
import InvitesTable from '../InvitesTable/InvitesTable';
import ManagersFilters from "../ManagersFilters/ManagersFilters";
import TableContainer from "../TableContainer/TableContainer";
import "./Access.scss";

const EMPTY_EMPLOYEE_FILTERS = {};
const EMPTY_MANAGER_FILTERS = {};

const sortManagersByNamePtBR = (data) => {
  if (!data || !Array.isArray(data.items)) return data;
  const sortedItems = [...data.items].sort((a, b) => {
    const nameA = a?.manager?.name || "";
    const nameB = b?.manager?.name || "";
    return nameA.localeCompare(nameB, "pt-BR", { sensitivity: "base" });
  });
  return { ...data, items: sortedItems };
};

const Access = () => {
  const [page, setPage] = useState(1);
  const [pageManagers, setPageManagers] = useState(1);
  const [uploadVisible, setUploadVisible] = useState(false);
  const [showPopUp, setShowPopUp] = useState(false);
  const [file, setFile] = useState("");
  const [employeeFilters, setEmployeeFilters] = useState(EMPTY_EMPLOYEE_FILTERS);
  const [managerFilters, setManagerFilters] = useState(EMPTY_MANAGER_FILTERS);
  const companyId = getItemFromStorage("COMPANY_ID");
  const { type } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const employees = useSelector(state => state.employees);
  const managers = useSelector(state => state.managers);
  const demo = useSelector(state => state.demo);
  const accesses = useSelector(state => state.accesses?.data);
  const isRootManager = useMemo(
    () => accesses.find(item => item?.company?.id === Number(companyId)),
    [accesses, companyId]
  );

  const handleEmployeeFiltersChange = useCallback((partial) => {
    setPage(1);
    setEmployeeFilters((current) => {
      const next = { ...current, ...partial };
      Object.keys(next).forEach((key) => {
        if (next[key] === undefined || next[key] === "") {
          delete next[key];
        }
      });
      return next;
    });
  }, []);

  const handleResetEmployeeFilters = useCallback(() => {
    setPage(1);
    setEmployeeFilters(EMPTY_EMPLOYEE_FILTERS);
  }, []);

  const employeeApiFilters = useMemo(() => {
    const { managerDisplay, ...rest } = employeeFilters;
    return rest;
  }, [employeeFilters]);

  const handleManagerFiltersChange = useCallback((partial) => {
    setPageManagers(1);
    setManagerFilters((current) => {
      const next = { ...current, ...partial };
      Object.keys(next).forEach((key) => {
        if (next[key] === undefined || next[key] === "") {
          delete next[key];
        }
      });
      return next;
    });
  }, []);

  const handleResetManagerFilters = useCallback(() => {
    setPageManagers(1);
    setManagerFilters(EMPTY_MANAGER_FILTERS);
  }, []);

  const handleEmployeesCsvSubmission = () => {
    const payload = {
      file
    };
    dispatch(addInvestors(payload))
      .then(() => {
        setShowPopUp(true);
      })
      .catch(() => {});
  }

  const getTableData = () => {
    if (demo.isDemoMode) {
      return type === "gestores" ? demo.data?.managers[pageManagers - 1] : demo.data?.employees[page - 1]
    } else {
      return type === "gestores" ? sortManagersByNamePtBR(managers.data) : employees.data
    }
  }

  useEffect(() => {
    dispatch(getAccesses());
  }, [dispatch]);

  useEffect(() => {
    const isAuth = isUserAuthenticated();
    const isDemo = demo.isDemoMode;

    if (!isAuth && !isDemo) {
      history.push("/login");
    }

    if (isDemo && !isAuth) {
      dispatch(getDemo());
    }

    if (isAuth && type === "convites-gestores" && isRootManager?.level === "MANAGER") {
      history.push("/acessos/convites-gestores");
    }

    if (isAuth && type === "gestores" && isRootManager?.level === "MANAGER") {
      history.push("/acessos/colaboradores");
    }

    if (isAuth && type === "colaboradores" && isRootManager) {
      const filters = { page, ...employeeApiFilters };
      dispatch(getEmployees(filters)).catch(() => { /* error already captured in store */ });
    }

    if (isAuth && type === "gestores" && isRootManager?.level !== "MANAGER") {
      const filters = { page: pageManagers, ...managerFilters };
      dispatch(getManagers(filters));
    }
  }, [demo.isDemoMode, dispatch, history, isRootManager, page, pageManagers, type, employeeApiFilters, managerFilters]);

  return (
    <section className="Access">
      <AccessHeader
        uploadVisible={uploadVisible}
        setUploadVisible={setUploadVisible}
        active={uploadVisible}
        isRootManager={isRootManager}
      />
      {type === "colaboradores" && !uploadVisible && !demo.isDemoMode && (
        <EmployeesFilters
          filters={employeeFilters}
          onChange={handleEmployeeFiltersChange}
          onReset={handleResetEmployeeFilters}
        />
      )}
      {type === "gestores" && !uploadVisible && !demo.isDemoMode && (
        <ManagersFilters
          filters={managerFilters}
          onChange={handleManagerFiltersChange}
          onReset={handleResetManagerFilters}
        />
      )}
      {type === "gestores"
        ? <TableContainer data={getTableData()} setPage={setPageManagers} hideTable={uploadVisible} isLoading={!demo.isDemoMode && managers.isFetching} />
        : type === "colaboradores"
          ? <TableContainer data={getTableData()} setPage={setPage} hideTable={uploadVisible} isLoading={!demo.isDemoMode && employees.isFetching} />
          : <InvitesTable data={getTableData()} setPage={setPage} hideTable={uploadVisible} />}
      <InvitationByCsv
        active={uploadVisible}
        file={file} setFile={setFile}
        closeUploadArea={() => setUploadVisible(false)}
        onSubmit={handleEmployeesCsvSubmission}
        type={"colaboradores"}
      />
      <PopUpSuccess
        active={showPopUp}
        closePopUp={() => history.push("/acessos/colaboradores")}
      >
        <p>Convite enviado aos colaboradores!</p>
      </PopUpSuccess>
    </section>
  );
};

export default Access;
